import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["align", "animate", "children", "onOpenStart", "onOpenComplete", "onCloseStart", "onCloseComplete", "onWheel", "panelKey", "rootNode", "scrollElementRef", "width", "maxWidth", "containerZIndex", "_dontMakeNewLayer", "panelStylesOverrides", "cssStyles"];
import { Component } from 'react';
import memoizeOne from 'react-utils/memoizeOne';
import { Provider as PanelComponentRegistrationProvider } from '../../context/PanelComponentRegistrationContext';
import * as PanelContext from '../../context/PanelContext';
import { constrainVerticalWheelEvent } from '../../utils/EventHandlers';
import { ShareScrollElement } from '../ScrollingColumn/ShareScrollElement';
import { Layer } from '../Layer/Layer';
import { percentToVW } from '../../utils/styles';
import { PanelTransition } from '../PanelTransition/PanelTransition';
import { SandboxContext } from '../../utils/SandboxContext';
import styled from 'foundations-theming/utils';
import { deepMerge } from '../../utils/deepMerge';
import { usePanelPanelNavigatorContext } from '../PanelNavigator/PanelNavigatorProvider';
import { getPanelTransitionProps } from '../PanelNavigator/panelUtils';
import { withTokenProps } from '../../../utils/withTokenProps';
import { JUPITER_LAYER } from '../../constants';
import { MIN_WIDTH } from '../../../constants/nonTokenisedValues';
import { RTEContextConsumer } from '../../context/RTEContext';
import { ResetContext } from '../../context/ResetContext';
import { jsx as _jsx } from "react/jsx-runtime";
const inPanelContext = {
  inPanel: true,
  inModal: false
};
const StyledPanel = styled.div.withConfig({
  displayName: "Panel__StyledPanel",
  componentId: "pt311z-0"
})(({
  theme,
  $isSandboxed,
  $align,
  $maxWidth,
  $width,
  $panelStylesOverrides = {},
  $cssStyles
}) => {
  const positionStyles = $align === 'left' ? {
    left: 0
  } : {
    right: 0
  };
  return deepMerge(Object.assign({}, positionStyles, {
    display: 'inline-flex',
    flexDirection: 'row',
    background: theme.color['fill-surface-default'],
    border: `${theme.borderWidth['transitional-2']} solid ${theme.color['border-container-default']}`,
    boxShadow: theme.boxShadow['300'],
    height: '100%',
    maxWidth: $maxWidth,
    minWidth: MIN_WIDTH.PANEL_CONTAINER,
    overflowX: 'hidden',
    overflowY: 'auto',
    width: $width,
    zIndex: JUPITER_LAYER
  }, !!$isSandboxed && {
    bottom: 0,
    position: 'absolute',
    top: 0
  }, $cssStyles), $panelStylesOverrides);
});
const StyledPanelContainer = styled.div.withConfig({
  displayName: "Panel__StyledPanelContainer",
  componentId: "pt311z-1"
})(["display:flex;flex-direction:column;min-height:100%;width:100%;"]);
export const PANEL_DEFAULT_WIDTH = '30%';
class PanelComponent extends Component {
  constructor(props) {
    super(props);
    this.handleWheel = evt => {
      // Allow a custom wheel handler to override this one
      const {
        onWheel,
        scrollElementRef
      } = this.props;
      if (typeof onWheel === 'function') {
        onWheel(evt);
        return;
      }
      constrainVerticalWheelEvent(evt, scrollElementRef.current);
    };
    this.state = {
      bodyCount: 0,
      headerCount: 0
    };
    this._getTransitionProps = memoizeOne(getPanelTransitionProps);
    this._isMounted = true; // Not technically, but child context funcs are called before didMount.
    // Track the number of PanelHeader and PanelBody instances this panel contains. This code
    // should be removed once all headerless/bodyless panels are eliminated.
    this._panelComponentRegistrationContextValue = {
      registerPanelBody: () => {
        if (this._isMounted) {
          this.setState(state => ({
            bodyCount: state.bodyCount + 1
          }));
        }
      },
      unregisterPanelBody: () => {
        if (this._isMounted) {
          this.setState(state => ({
            bodyCount: state.bodyCount - 1
          }));
        }
      },
      registerPanelHeader: () => {
        if (this._isMounted) {
          this.setState(state => ({
            headerCount: state.headerCount + 1
          }));
        }
      },
      unregisterPanelHeader: () => {
        if (this._isMounted) {
          this.setState(state => ({
            headerCount: state.headerCount - 1
          }));
        }
      }
    };
  }
  componentDidMount() {
    const {
      scrollElementRef
    } = this.props;

    // Only apply focus if the panel is not sandboxed in UI Library.
    if (!this.context.sandboxed) {
      scrollElementRef.current.focus(); // adding focus so users can tab into panel
    }

    // Using the React `onWheel` event would be restrictive in Chrome (#6272)
    scrollElementRef.current.addEventListener('wheel', this.handleWheel);
  }
  componentWillUnmount() {
    this._isMounted = false;
  }
  render() {
    const _this$props = this.props,
      {
        align = 'right',
        animate = true,
        children,
        onOpenStart,
        onOpenComplete,
        onCloseStart,
        onCloseComplete,
        rootNode,
        scrollElementRef,
        width = PANEL_DEFAULT_WIDTH,
        maxWidth = '100%',
        containerZIndex = '1111',
        _dontMakeNewLayer = false,
        panelStylesOverrides,
        cssStyles
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const {
      sandboxed
    } = this.context;
    const computedWidth = percentToVW(width);
    // For numeric pixel widths, use 'none' to prevent wrapper conflicts
    // For relative widths (percentages), preserve the maxWidth constraint
    const computedMaxWidth = typeof width === 'number' ? 'none' : percentToVW(maxWidth);
    const renderedPanel = /*#__PURE__*/_jsx(ResetContext, {
      children: /*#__PURE__*/_jsx(RTEContextConsumer, {
        children: rteContext => /*#__PURE__*/_jsx(PanelContext.Provider, {
          value: inPanelContext,
          children: /*#__PURE__*/_jsx(StyledPanel, Object.assign({
            ref: scrollElementRef,
            tabIndex: 0,
            $isSandboxed: sandboxed,
            $align: align,
            $width: computedWidth,
            $maxWidth: computedMaxWidth,
            $panelStylesOverrides: panelStylesOverrides,
            $cssStyles: cssStyles
            // Necessary for TinyMCE to prevent blur on the editor when component is clicked
            // https://git.hubteam.com/HubSpot/structured-content/issues/702
          }, rteContext, rest, {
            children: /*#__PURE__*/_jsx(PanelComponentRegistrationProvider, {
              value: this._panelComponentRegistrationContextValue,
              children: /*#__PURE__*/_jsx(StyledPanelContainer, {
                children: children
              })
            })
          }))
        })
      })
    });
    if (_dontMakeNewLayer || sandboxed) {
      return renderedPanel;
    }
    return /*#__PURE__*/_jsx(Layer, {
      "data-layer-for": "Panel",
      rootNode: rootNode,
      Transition: PanelTransition,
      transitionProps: this._getTransitionProps(onOpenStart, onOpenComplete, onCloseStart, onCloseComplete, align, animate, containerZIndex),
      children: renderedPanel
    });
  }
}
PanelComponent.contextType = SandboxContext;
const PanelWrapper = props => {
  var _panelPanelNavigatorC;
  const panelPanelNavigatorContext = usePanelPanelNavigatorContext();
  const panelStylesOverrides = (_panelPanelNavigatorC = panelPanelNavigatorContext === null || panelPanelNavigatorContext === void 0 ? void 0 : panelPanelNavigatorContext.panelStylesOverrides) !== null && _panelPanelNavigatorC !== void 0 ? _panelPanelNavigatorC : {};
  return /*#__PURE__*/_jsx(PanelComponent, Object.assign({}, props, {
    panelStylesOverrides: panelStylesOverrides
  }));
};
export const Panel = ShareScrollElement(withTokenProps(PanelWrapper));